<template>
  <div>
    <main-header-login />
    <div class="spl-anonymous-tracking__reference">
      <form-input v-model="selectedReference" placeholder="Reference" />
      <form-button @onClick="setRouteAndSearch">
        Search
      </form-button>
    </div>
    <div v-if="foundParcels.length === 0 && !isLoading">
      <h3>No parcel found under this Reference</h3>
    </div>
    <div v-if="foundParcels.length > 0" class="spl-anonymous-tracking">
      <div
        class="spl-anonymous-tracking__header"
        :class="{
          'spl-anonymous-tracking__header--on-time': selectedParcel.Delivery_Timestamp && isParcelOnTime(selectedParcel)
        }">
        <h3>{{ headerText }}</h3>
      </div>
      <div v-if="foundParcels.length > 1" class="spl-anonymous-tracking__select-parcel">
        <form-label>Select a Parcel</form-label>
        <form-filtered-select
          v-if="foundParcels.length > 1"
          :options="parcelOptions"
          :selected-key="selectedParcelKey"
          @input="selectParcel" />
      </div>

      <div class="padding-wrapper">
        <div class="row">
          <!-- *********************** Maps Column *********************************** -->
          <div
            v-if="showMap"
            class="col4 sm-col12">
            <parcel-map
              :center="mapCenter"
              :url="mapUrl" />
          </div>
          <!-- *********************** Timeline Column *********************************** -->
          <div
            :class="{ col8: showMap, col12: !showMap }"
            class="sm-col12">
            <parcel-timeline
              :timeline-data="timelineData" />
          </div>
        </div>
      </div>

      <div class="spl-anonymous-tracking__event-table">
        <h3 style="padding-top: inherit; padding-bottom: inherit;">
          Events
        </h3>
        <table>
          <thead>
            <tr>
              <th>Milestone</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(event, index) in timelineData"
              :key="'event' + index"
              :class="{'spl-anonymous-tracking__event-table--delayed': event.Is_Delayed}">
              <td>{{ event.Label }}</td>
              <td>{{ formatDate(event.Timestamp, true) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="spl-anonymous-tracking__container">
        <div class="spl-anonymous-tracking__container__block">
          <h3>General</h3>
          <ul>
            <li>
              <span>Counter Number</span>{{ selectedParcel.COUNTER_NUMBER }}
            </li>
            <li>
              <span>Country Name</span>{{ selectedParcel.Country_Name }}
            </li>
            <li>
              <span>Service Level</span>{{ selectedParcel.Service_Level_Name }}
            </li>
            <li>
              <span>Direction</span>{{ selectedParcel.Is_Forward ? 'Forward' : 'Return' }}
            </li>
            <li v-if="selectedParcel.Booked_Pickup_Date">
              <span>Booked pickup</span>{{ formatDate(selectedParcel.Booked_Pickup_Date, false) }}
            </li>
            <li v-if="showExternalLink">
              <span>Forwarder tracking page</span><external-tracker :parcel-details="selectedParcel" />
            </li>
          </ul>
        </div>
        <div class="spl-anonymous-tracking__container__block">
          <h3>References</h3>
          <ul>
            <li v-for="(reference, index) in selectedParcel.References" :key="'reference'+index">
              <span>{{ reference.Reference_Type }}</span>{{ reference.Reference_Text }}
            </li>
          </ul>
        </div>
        <div class="spl-anonymous-tracking__container__block">
          <h3>Parcel</h3>
          <ul>
            <li>
              <span>Weight</span>{{ selectedParcel.Weight }} kg
            </li>
            <li v-if="selectedParcel.Volume > 0">
              <span>Volume</span>{{ selectedParcel.Volume }} m<sup>3</sup>
            </li>
            <li v-if="selectedParcel.Length > 0 || selectedParcel.Width > 0 || selectedParcel.Height > 0">
              <span>Dimensions (LxWxH)</span>{{ selectedParcel.Length + ' cm x ' + selectedParcel.Width + ' cm x ' + selectedParcel.Height + ' cm' }}
            </li>
          </ul>
        </div>
        <div v-if="selectedParcel.Delivery_Address" class="spl-anonymous-tracking__container__block">
          <h3>Delivery</h3>
          <ul>
            <li>
              <span>Country</span>{{ selectedParcel.Delivery_Address.Country_Name }}
            </li>
            <li>
              <span>Postal Code</span>{{ selectedParcel.Delivery_Address.Postal_Code }}
            </li>
            <li>
              <span>City</span>{{ selectedParcel.Delivery_Address.City }}
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="spl-load-img" />
  </div>
</template>

<script>
import webApi from '@/webApi'
import { cloneDeep, sortBy, find, map } from 'lodash'
import moment from 'moment'
import constants from '@/constants'
export default {
  data () {
    return {
      foundParcels: [],
      selectedParcel: {},
      timelineData: [],
      selectedReference: null,
      selectedParcelKey: null,
      isLoading: false,
      mapCenter: {},
      mapUrl: ''
    }
  },
  computed: {
    parcelOptions () {
      if (!this.foundParcels) {
        return [{ value: 1, label: 'none' }]
      }
      return map(this.foundParcels, (parcel) => {
        var label = parcel.COUNTER_NUMBER
        if (this.$mq === 'sm') {
          label += ' (' + (parcel.Is_Forward ? 'F' : 'R') + ')'
        } else {
          label += ' (' + (parcel.Is_Forward ? 'Forward' : 'Return') + ' - ' + this.formatDate(parcel.Booked_Pickup_Date, false) + ')'
        }
        return { value: parcel.Parcel_ID, label: label }
      })
    },
    headerText () {
      var event = this.selectedParcel.Current_Status_Event_Name

      if (event.length > 4 && event.split('.').length > 1) {
        if (event.indexOf('POS.') !== -1) {
          return 'Parcel ' + this.selectedParcel.COUNTER_NUMBER + ' is in ' + event.substring(4) + ' (' + this.formatDate(this.selectedParcel.Current_Status_Event_Timestamp) + ')'
        } else {
          event = event.substring(4)
        }
      }
      return 'Current status of parcel ' + this.selectedParcel.COUNTER_NUMBER + ' is ' + event + ' (' + this.formatDate(this.selectedParcel.Current_Status_Event_Timestamp) + ')'
    },
    showMap: function () {
      return this.hasGeoPositionOrUrl()
    },
    showExternalLink: function () {
      return this.selectedParcel.Country_ID !== constants.COUNTRY_ID. ITALY ||
              (this.selectedParcel.Customer_ID !== constants.CUSTOMER_ID.SIEMENS_DIAGNOSTICS &&
                this.selectedParcel.Customer_ID  !== constants.CUSTOMER_ID. SIEMENS_HEALTHCARE)
    }
  },
  mounted () {
    this.searchForReference()
  },
  methods: {
    isParcelOnTime (parcel) {
      if (!parcel.Delivery_Timestamp) {
        return false
      }
      return moment(parcel.Delivery_Timestamp).isBefore(moment(parcel.Close_Deadline))
    },
    setRouteAndSearch () {
      this.$router.replace({ name: 'tracking', query: { reference: this.selectedReference } })
      this.searchForReference(this.selectedReference)
    },
    searchForReference (reference) {
      if (!this.$route.query.reference && !reference) {
        return
      }
      var searchReference = reference || this.$route.query.reference
      this.isLoading = true
      this.mapCenter = {}
      this.mapUrl = ''
      webApi.anonymous_tracking.search_Parcels(searchReference, this.$route.query.referenceType, this.$route.query.customer).then((result) => {
        this.isLoading = false
        this.foundParcels = result.data
        for (const parcel of this.foundParcels) {
          for (const key in constants.REFERENCE_TYPE_NAME) {
            if (Object.prototype.hasOwnProperty.call(constants.REFERENCE_TYPE_ID, key)) {
              const reference = constants.REFERENCE_TYPE_ID[key]
              const foundReference = find(parcel.References, { Reference_Type_ID: reference })
              if (foundReference) {
                parcel[key] = foundReference.Reference_Text
              }
            }
          }
        }
        this.prepareParcelData(result.data[0])
      }).catch(() => {
        this.isLoading = false
      })
    },
    selectParcel (value) {
      this.prepareParcelData(find(this.foundParcels, { Parcel_ID: value }))
    },
    prepareParcelData (parcel) {
      if (!parcel) {
        this.selectedParcel = {}
        this.timelineData = []
        return
      }

      this.selectedParcel = parcel
      this.selectedParcelKey = parcel.Parcel_ID
      this.selectedParcel.Events = sortBy(this.selectedParcel.Events, (event) => { return new Date(event.Timestamp) })
      this.prepareTimeline(parcel)
      this.get_Gps_Current_Position(parcel.Parcel_ID)
      this.$forceUpdate()
    },
    prepareTimeline (data) {
      var events = cloneDeep(data.Events)
      var deadline = moment(data.Close_Deadline)
      events.push({
        Milestone_Name: 'Deadline',
        Timestamp: data.Close_Deadline
      })
      events = sortBy(events, (event) => { return new Date(event.Timestamp) })
      this.timelineData = []
      for (let i = 0; i < events.length; i++) {
        const event = events[i]
        const timeLineEvent = {
          Label: event.Milestone_Name ? event.Milestone_Name : event.Event_Info_Name,
          Timestamp: event.Timestamp
        }

        timeLineEvent.Is_Close_Deadline = event.Milestone_Name === 'Deadline'
        timeLineEvent.Is_Delayed = moment(event.Timestamp).isAfter(deadline) && event.Event_Info_Name !== 'STS.TechnicianPickup'

        if (event.Timestamp === data.Current_Status_Event_Timestamp && event.Event_Info_Name === data.Current_Status_Event_Name) {
          timeLineEvent.Is_Current_Status_Event = true
        } else {
          timeLineEvent.Is_Current_Status_Event = false
        }
        this.timelineData.push(timeLineEvent)
      }
    },
    formatDate (date, useTime) {
      if (useTime) {
        return moment(date).format('YYYY-MM-DD HH:mm')
      } else {
        return moment(date).format('YYYY-MM-DD')
      }
    },
    // get the GPS coordinates
    get_Gps_Current_Position (parcelKey) {
      webApi.anonymous_tracking.get_Current_Gps_Position_By_Parcel_Id(parcelKey)
        .then((response) => {
          // Get the json from the response
          const current_position_json = response.data
          if (current_position_json != null && current_position_json.Latitude !== 0.0 && current_position_json.Longitude !== 0.0) {
            this.mapCenter = { lat: current_position_json.Latitude, lng: current_position_json.Longitude }
            this.mapUrl = ''
          } else
          if (current_position_json !== null && current_position_json.Map_Url !== '') {
            this.mapCenter = {}
            this.mapUrl = current_position_json.Map_Url
          }
        }).catch((response) => {
          this.isLoading = false
        })
    },
    // ************************ Check if there are available coordinates in events ************************
    hasGeoPositionOrUrl () {
      if (this.hasMapCenter() || this.hasMapUrl()) {
        return true
      } else {
        return false
      }
    },
    hasMapCenter () {
      return this.mapCenter && this.mapCenter.lat != null && this.mapCenter.lng != null
    },
    hasMapUrl () {
      return this.mapUrl && this.mapUrl !== ''
    }
  }
}
</script>

<style lang="scss">
  @import '../variables.scss';
.spl-anonymous-tracking {
  &__reference {
    position: fixed;
    right: 0px;
    top: 14px;
    display: flex;
    flex-direction: row;
    z-index: 10000;

    & .spl-input {
      width: 200px;
      margin-right: 20px;
    }
  }

  &__select-parcel {
    margin: 10px 70px;
    max-width: 400px;
    & .spl-singleselect {
      z-index: 1005;
    }
  }
  &__header {
    background-color: $parcelTimelineBackgroundColor;
    margin: 10px 70px;
    padding: 10px;
    &--on-time {
      background-color: $successGreenBackground;
    }
  }
  &__event-table {
    display: none
  }
  &__timeline {
    margin: 0 70px;
  }
  &__container {
    margin: 0 51px 0 63px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    &__block {
      background-color: $parcelTimelineBackgroundColor;
      padding: 7px;
      margin: 7px;
      width: 48%;

      & ul {
        padding: 0;

        & li {
          list-style: none;
          line-height: 1.5em;
          font-size: 13px;

          & span {
            display: inline-block;
            width: 180px;
            font-weight: 600;
          }
        }
      }
    }
  }
  @include mobile() {
    &__reference {
      position: relative;
      background-color: black;
      display: flex;
      flex-direction: row;
      top: 0px;
      padding: 7px;
      z-index: inherit;
      width: 100%;

      & .spl-input {
        flex-grow: 2;
        width: 60%;
        margin-right: 20px;
      }
      & .spl-button {
        width: 56px;
        min-width: 56px;
        flex-grow: 1;
        padding: 5px 10px;
        margin-right: 7px;
        height: 41px;
      }
    }

    &__select-parcel {
      position: fixed;
      background-color: $grey_20;
      margin: 0;
      padding: 10px;
      bottom: 0;
      height: 100px;
      max-width: none;
      width: 100%;
    }

    &__header {
      background-color: $parcelTimelineBackgroundColor;
      margin: 7px;
      padding: 7px;
      &--on-time {
        background-color: $successGreenBackground;
      }
    }

    &__container {
      margin: 5px 5px 200px 5px;
      &__block {
        width: 100%;
        padding: 10px;
        & h3 {
          font-size: 18px;
          margin: 5px 0;
        }
        & ul {
          & li {
            margin-bottom: 10px;
            font-size: 14px;

            & span {
              display: block
            }
          }
        }
      }
    }
    &__event-table {
      display: block;
      background-color: $parcelTimelineBackgroundColor;
      margin: 7px;
      padding: 7px;

      &--delayed {
        color: $red;
      }

      table {
        border-spacing: 0;
        table-layout: fixed;
        thead {
          th {
            top: 0;
            text-align: left;
            font-family: $mono;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            line-height: 12px;
            background-color: $tableHeaderBackgroundColor;
            padding: 0px 5px  0px 5px;
            height: 40px;
            border-bottom: 1px solid $tableHeaderBorderColor;
          }

        }
        tbody {

          tr {
            background-color: $tableRowBackgroundColor;
          }

          td {
            border-style: solid;
            border-width: 0 1px 1px 0;
            border-color: $tableRowBorderLowColor;
            border-bottom-color: black;
            font-size: 13px;
            padding: 5px;
            padding-top: 8px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 150px;
          }

          td:first-child {
            border-left-width: 1px;
          }
        }
      }
    }
  }
}
</style>
